import { useEffect, useState, useRef, useCallback } from 'react';

interface PropertyForMap {
  id: number;
  space: {
    road_address: string;
  } | null;
  rent: {
    weekly_rent: number;
  } | null;
}

interface PropertyListMapProps {
  properties: PropertyForMap[];
  hoveredPropertyId: number | null;
  onMarkerClick: (propertyId: number) => void;
}

interface GeocodedProperty {
  id: number;
  lat: number;
  lng: number;
  weeklyRent: number;
}

// 가격 마커 HTML (직방 스타일)
const createPriceMarkerHtml = (price: number, isHovered: boolean): string => {
  const priceText = price.toLocaleString('ko-KR');
  const bgColor = isHovered ? '#2962FF' : '#fff';
  const textColor = isHovered ? '#fff' : '#222';
  const borderColor = isHovered ? '#2962FF' : '#ddd';
  const arrowColor = isHovered ? '#2962FF' : '#fff';
  const arrowBorderColor = isHovered ? '#2962FF' : '#ddd';
  return `<div style="position: relative; display: inline-block; cursor: pointer;">
    <div style="
      background-color: ${bgColor};
      color: ${textColor};
      border: 1.5px solid ${borderColor};
      border-radius: 16px;
      padding: 4px 10px;
      font-size: 13px;
      font-weight: 700;
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0,0,0,0.15);
      text-align: center;
    ">${priceText}</div>
    <div style="
      width: 0; height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid ${arrowBorderColor};
      margin: 0 auto;
    "></div>
    <div style="
      position: absolute; bottom: 1.5px; left: 50%;
      transform: translateX(-50%);
      width: 0; height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid ${arrowColor};
    "></div>
  </div>`;
};

// localStorage 지오코딩 캐시 (ExposureMap/AddressMap과 공유)
const CACHE_KEY = 'str_geocode_cache';
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 30일

const loadCache = (): Record<string, { lat: number; lng: number; ts: number }> => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveCache = (cache: Record<string, { lat: number; lng: number; ts: number }>) => {
  localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
};

const geocodeAddress = (
  geocoder: kakao.maps.services.Geocoder,
  address: string
): Promise<{ lat: number; lng: number } | null> => {
  const cache = loadCache();
  const entry = cache[address];
  if (entry && Date.now() - entry.ts < CACHE_TTL) {
    return Promise.resolve({ lat: entry.lat, lng: entry.lng });
  }

  return new Promise((resolve) => {
    geocoder.addressSearch(address, (result, status) => {
      if (status === kakao.maps.services.Status.OK && result.length > 0) {
        const coords = { lat: parseFloat(result[0].y), lng: parseFloat(result[0].x) };
        const freshCache = loadCache();
        freshCache[address] = { ...coords, ts: Date.now() };
        saveCache(freshCache);
        resolve(coords);
      } else {
        resolve(null);
      }
    });
  });
};

const PropertyListMap = ({ properties, hoveredPropertyId, onMarkerClick }: PropertyListMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<kakao.maps.Map | null>(null);
  const overlaysRef = useRef<Map<number, kakao.maps.CustomOverlay>>(new Map());
  const [geocodedProperties, setGeocodedProperties] = useState<GeocodedProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);

  // 카카오맵 SDK 로드
  useEffect(() => {
    const SDK_URL = 'https://dapi.kakao.com/v2/maps/sdk.js?appkey=032aee3d910c25f34f64cd2fb353616e&libraries=services&autoload=false&_v=20260301';

    const initSdk = () => {
      if (window.kakao && window.kakao.maps) {
        window.kakao.maps.load(() => setSdkReady(true));
        return;
      }
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.onload = () => {
        if (window.kakao && window.kakao.maps) {
          window.kakao.maps.load(() => setSdkReady(true));
        } else {
          setSdkError('스크립트는 로드됐으나 kakao 객체가 생성되지 않았습니다. API 키를 확인해주세요.');
          setLoading(false);
        }
      };
      script.onerror = () => {
        setSdkError('카카오맵 SDK 스크립트를 불러올 수 없습니다. 네트워크 연결을 확인해주세요.');
        setLoading(false);
      };
      document.head.appendChild(script);
    };

    const timer = setTimeout(initSdk, 1000);
    if (window.kakao && window.kakao.maps) {
      clearTimeout(timer);
      initSdk();
    }
    return () => clearTimeout(timer);
  }, []);

  // 지도 초기화
  useEffect(() => {
    if (!sdkReady || !containerRef.current || mapRef.current) return;

    const center = new kakao.maps.LatLng(37.5665, 126.978);
    const map = new kakao.maps.Map(containerRef.current, {
      center,
      level: 8,
    });
    mapRef.current = map;

    return () => {
      overlaysRef.current.forEach(o => o.setMap(null));
      overlaysRef.current.clear();
      mapRef.current = null;
    };
  }, [sdkReady]);

  // 병렬 지오코딩
  const geocodeAll = useCallback(async () => {
    if (!sdkReady) return;

    setLoading(true);
    const geocoder = new kakao.maps.services.Geocoder();

    const results = await Promise.all(
      properties
        .filter(p => p.space?.road_address && p.rent)
        .map(async (p) => {
          const coords = await geocodeAddress(geocoder, p.space!.road_address);
          return coords
            ? { id: p.id, lat: coords.lat, lng: coords.lng, weeklyRent: p.rent!.weekly_rent }
            : null;
        })
    );

    setGeocodedProperties(results.filter(Boolean) as GeocodedProperty[]);
    setLoading(false);
  }, [properties, sdkReady]);

  useEffect(() => {
    geocodeAll();
  }, [geocodeAll]);

  // 오버레이(마커) 생성
  useEffect(() => {
    const map = mapRef.current;
    if (!map || geocodedProperties.length === 0) return;

    // 기존 오버레이 제거
    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current.clear();

    const bounds = new kakao.maps.LatLngBounds();

    geocodedProperties.forEach(gp => {
      const position = new kakao.maps.LatLng(gp.lat, gp.lng);
      bounds.extend(position);

      const isHovered = gp.id === hoveredPropertyId;
      const content = document.createElement('div');
      content.innerHTML = createPriceMarkerHtml(gp.weeklyRent, isHovered);
      content.style.cursor = 'pointer';
      content.onclick = () => onMarkerClick(gp.id);

      const overlay = new kakao.maps.CustomOverlay({
        position,
        content,
        clickable: true,
        xAnchor: 0.5,
        yAnchor: 1.0,
        zIndex: isHovered ? 10 : 1,
      });
      overlay.setMap(map);
      overlaysRef.current.set(gp.id, overlay);
    });

    if (!bounds.isEmpty()) {
      map.setBounds(bounds, 60, 60, 60, 60);
    }
  }, [geocodedProperties, hoveredPropertyId, onMarkerClick]);

  // hover 변경 시 오버레이 업데이트
  useEffect(() => {
    overlaysRef.current.forEach((overlay, id) => {
      const gp = geocodedProperties.find(p => p.id === id);
      if (!gp) return;

      const isHovered = id === hoveredPropertyId;
      const content = document.createElement('div');
      content.innerHTML = createPriceMarkerHtml(gp.weeklyRent, isHovered);
      content.style.cursor = 'pointer';
      content.onclick = () => onMarkerClick(gp.id);
      overlay.setContent(content);
      overlay.setZIndex(isHovered ? 10 : 1);
    });
  }, [hoveredPropertyId, geocodedProperties, onMarkerClick]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {sdkError && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(245,245,245,0.95)',
          zIndex: 1000,
        }}>
          <p style={{ color: '#e53935', fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>
            카카오맵을 불러올 수 없습니다
          </p>
          <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', lineHeight: '1.6' }}>
            {sdkError}
          </p>
        </div>
      )}
      {!sdkError && loading && geocodedProperties.length === 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(245,245,245,0.8)',
          zIndex: 1000,
        }}>
          <p style={{ color: '#999', fontSize: '14px' }}>지도 로딩 중...</p>
        </div>
      )}
    </div>
  );
};

export default PropertyListMap;
